import { getCursorConfig, CURSOR_CONSTANTS } from "./cursorBehaviors.js";

export function setupCustomCursor(k) {
    k.setCursor("none");

    // Helper to turn your config {r, g, b} objects into Kaplay RGB components
    const toKColor = (colorObj) => k.rgb(colorObj.r, colorObj.g, colorObj.b);

    const cursor = k.add([
        k.sprite("arrowCursor"),
        k.scale(CURSOR_CONSTANTS.baseScale),
        k.pos(-100,-100),
        k.anchor("center"),
        k.color(toKColor(CURSOR_CONSTANTS.baseColor)),
        k.z(1000),
        k.anchor("topleft"),
        k.fakeMouse(), // The fakeMouse() component will make it movable with a real mouse
        "custom_cursor"
    ]);

    const secondaryCursor = k.add([
        k.sprite("arrowCursor"),
        k.scale(CURSOR_CONSTANTS.interactionScale),
        k.pos(0, 0),
        k.anchor("topleft"),
        k.z(1000),
        k.color(255, 255, 255),
        k.opacity(0)
    ]);

    // Semantic State
    cursor.equippedCard = null; // null, "PAN", "EN_JOUE", "TIREUR_DELITE"
    cursor.hoverContext = "NONE"; // "NONE", "CARD", "SLOT_VALID", "SLOT_INVALID"
    cursor.suppressSecondary = false; // Do not display secondary cursor if true

    // Second cursor position
    cursor.secondaryTargetPos = null;

    // Internal State Management
    let pulseTimer = null;
    let scaleTween = null;

    cursor.refreshVisuals = () => {
        if (scaleTween) scaleTween.cancel();
        if (pulseTimer) { pulseTimer.cancel(); pulseTimer = null; }

        // Define the default cursor state
        let targetSprite = "arrowCursor";
        let targetAnchor = "topleft";
        let targetColor = toKColor(CURSOR_CONSTANTS.baseColor);
        let targetScale = CURSOR_CONSTANTS.baseScale;
        let showSecondary = false;

        // Override based on Hover Context
        if (cursor.hoverContext === "CARD" && cursor.equippedCard === null) {
            targetColor = toKColor(CURSOR_CONSTANTS.cardHoverColor);
            targetScale = CURSOR_CONSTANTS.interactionScale;
            targetAnchor = "topleft";

            let growing = true;
            pulseTimer = k.loop(0.35, () => {
                const t = growing ? 0.8 : 0.5;
                scaleTween = k.tween(cursor.scale.x, t, 0.3, v => cursor.scale = k.vec2(v), k.easings.easeInOutSine);
                growing = !growing;
            });
        }
        else {
            let lookupState = "idle";
            if (cursor.hoverContext === "SLOT_VALID") lookupState = "valid";
            if (cursor.hoverContext === "SLOT_INVALID") lookupState = "invalid";

            const config = getCursorConfig(cursor.equippedCard, lookupState);

            targetSprite = config.sprite;
            targetAnchor = config.anchor;
            targetColor = toKColor(config.color);
            targetScale = config.scale;

            if (config.secondary && !cursor.suppressSecondary) {
                showSecondary = true;
            }
        }

        // Apply
        cursor.use(k.sprite(targetSprite));
        cursor.use(k.anchor(targetAnchor));
        cursor.color = targetColor;

        // Animation
        scaleTween = k.tween(cursor.scale.x, targetScale, 0.15, v => cursor.scale = k.vec2(v), k.easings.easeOutBack);

        // Add secondary cursor if needed
        if (showSecondary && cursor.secondaryTargetPos) {
            secondaryCursor.use(k.sprite(targetSprite));
            secondaryCursor.use(k.anchor(targetAnchor));
            secondaryCursor.color = targetColor;

            // Snap it directly to the absolute world position
            secondaryCursor.pos = cursor.secondaryTargetPos;
            secondaryCursor.opacity = 1;
        } else {
            secondaryCursor.opacity = 0;
            secondaryCursor.pos = k.vec2(0, 0);
        }
    };

    // Public methods
    cursor.equip = (cardType) => {
        cursor.equippedCard = cardType;
        cursor.refreshVisuals();
    };

    cursor.hoverCard = () => {
        cursor.hoverContext = "CARD";
        cursor.refreshVisuals();
    };

    cursor.hoverSlot = (isValid, suppressSecondary = false, secondaryPos = null) => {
        cursor.hoverContext = isValid ? "SLOT_VALID" : "SLOT_INVALID";
        cursor.suppressSecondary = suppressSecondary;
        cursor.secondaryTargetPos = secondaryPos;
        cursor.refreshVisuals();
    };

    cursor.clearHover = () => {
        cursor.hoverContext = "NONE";
        cursor.refreshVisuals();
    };

    k.onMousePress(() => {
        if (scaleTween) scaleTween.cancel();
        cursor.scale = k.vec2(CURSOR_CONSTANTS.pressedScale);
    });

    k.onMouseRelease(() => {
        if (scaleTween) scaleTween.cancel();

        const scaleToUse = cursor.hoverContext !== "NONE" ? k.vec2(CURSOR_CONSTANTS.interactionScale) : k.vec2(CURSOR_CONSTANTS.baseScale);
        cursor.scale = k.vec2(scaleToUse);
    });

    return cursor;
}